<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import SectionCard from 'dashboard/components-next/section-card/SectionCard.vue';
import CustomAttribute from 'dashboard/components/CustomAttribute.vue';

const props = defineProps({
  category: { type: String, required: true },
  event: { type: Object, required: true },
  attributes: { type: Array, default: () => [] },
  title: { type: String, required: true },
  icon: { type: String, required: true },
  readOnly: { type: Boolean, default: false },
  collapsed: { type: Boolean, default: false },
  showRemoveBlock: { type: Boolean, default: false },
});

const emit = defineEmits([
  'updateAttribute',
  'deleteAttribute',
  'copyAttribute',
  'removeBlock',
  'toggleCollapse',
]);

const store = useStore();
const { t } = useI18n();

const formattedAgents = computed(() =>
  (store.getters['agents/getAgents'] || []).map(agent => ({
    id: agent.id,
    name: agent.name,
    email: agent.email,
    thumbnail: agent.thumbnail,
    availability_status: agent.availability_status,
  }))
);

const accountId = computed(() => store.getters.getCurrentAccountId);

const isArchivedAttribute = attr => Boolean(attr?.discarded_at);

const isAttributeReadOnly = attr =>
  props.readOnly || isArchivedAttribute(attr);

const showAttributeActions = attr =>
  !props.readOnly || isArchivedAttribute(attr);
</script>

<template>
  <SectionCard
    :title="title"
    :icon="icon"
    collapsible
    collapse-in-menu
    :collapse-label="t('PLANNING.SHOW.COLLAPSE')"
    :expand-label="t('PLANNING.SHOW.EXPAND')"
    :menu-actions="
      showRemoveBlock
        ? [
            {
              key: 'remove-block',
              label: t('PLANNING.SHOW.REMOVE_BLOCK'),
              icon: 'i-lucide-trash-2',
              danger: true,
            },
            {
              key: 'toggle-collapse',
              label: collapsed
                ? t('PLANNING.SHOW.EXPAND')
                : t('PLANNING.SHOW.COLLAPSE'),
              icon: collapsed ? 'i-lucide-chevron-down' : 'i-lucide-chevron-up',
            },
          ]
        : []
    "
    flush
    :collapsed="collapsed"
    @toggle-collapse="emit('toggleCollapse')"
    @menu-action="emit('removeBlock', category)"
  >
    <div class="mt-4 grid min-w-0 grid-cols-1 gap-x-6">
      <CustomAttribute
        v-for="attr in attributes"
        :key="attr.id"
        :label="attr.attribute_display_name"
        :description="attr.attribute_description"
        :values="attr.attribute_values || []"
        :value="event?.custom_attributes?.[attr.attribute_key] ?? ''"
        :show-actions="showAttributeActions(attr)"
        :read-only="isAttributeReadOnly(attr)"
        :archived="isArchivedAttribute(attr)"
        :attribute-type="attr.attribute_display_type"
        :attribute-regex="attr.regex_pattern"
        :regex-cue="attr.regex_cue"
        :attribute-key="attr.attribute_key"
        :agents="formattedAgents"
        :account-id="accountId"
        @update="(key, value) => emit('updateAttribute', key, value)"
        @delete="emit('deleteAttribute', $event)"
        @copy="emit('copyAttribute', $event)"
      />
    </div>
  </SectionCard>
</template>
